/* global $ */
import React, { useImperativeHandle, useEffect, useContext, useState, useMemo, useRef } from 'react';

import { searchForLabel } from '../../helpers/common.helpers';
import { RequestContext } from '../../context/Request.context';

import './PaymentButton.scss';

const getTid = (isExpress, isRenewal, isEmployerChange) => {
  if (isExpress) {
    if (isRenewal) {
      return import.meta.env.VITE_PAYREXX_TID_EXPRESS_RENEWAL;
    } else if (isEmployerChange) {
      return import.meta.env.VITE_PAYREXX_TID_EMPLOYER_CHANGE;
    } else {
      return import.meta.env.VITE_PAYREXX_TID_EXPRESS;
    }
  } else {
    if (isRenewal) {
      return import.meta.env.VITE_PAYREXX_TID_RENEWAL;
    } else if (isEmployerChange) {
      return import.meta.env.VITE_PAYREXX_TID_EMPLOYER_CHANGE;
    } else {
      return import.meta.env.VITE_PAYREXX_TID;
    }
  }
};

export const PaymentButton = React.forwardRef((props, ref) => {
  const { isRenewal, isEmployerChange, onClose } = props;
  const buttonRef = useRef(null);
  const isInitializedRef = useRef(false);
  const previousUrlRef = useRef('');
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const {
    resources: { countries },
    request,
  } = useContext(RequestContext);

  // Keep isExpress in state with proper initialization
  const [isExpress, setIsExpress] = useState(() => {
    try {
      const value = sessionStorage.getItem('regservices-form-values-accepted_additional_fee_agreement');
      return value !== null ? JSON.parse(value) : false;
    } catch (e) {
      console.error('Failed to parse sessionStorage item', e);
      return false;
    }
  });

  // Calculate tid based on isExpress state
  // const tid =
  //   isExpress && isRenewal
  //     ? import.meta.env.VITE_PAYREXX_TID_EXPRESS_RENEWAL
  //     : isExpress
  //       ? import.meta.env.VITE_PAYREXX_TID_EXPRESS
  //       : isRenewal
  //         ? import.meta.env.VITE_PAYREXX_TID_RENEWAL
  //         : import.meta.env.VITE_PAYREXX_TID;
  const tid = getTid(isExpress, isRenewal, isEmployerChange);

  // Listen for storage events to update isExpress
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'regservices-form-values-accepted_additional_fee_agreement') {
        try {
          const newValue = e.newValue !== null ? JSON.parse(e.newValue) : false;
          setIsExpress(newValue);
        } catch (e) {
          console.error('Failed to parse sessionStorage change', e);
          setIsExpress(false);
        }
      }
    };

    // Add event listener
    window.addEventListener('storage', handleStorageChange);

    // Poll for changes in the same window
    const intervalId = setInterval(() => {
      try {
        const currentValue = sessionStorage.getItem('regservices-form-values-accepted_additional_fee_agreement');
        const parsedValue = currentValue !== null ? JSON.parse(currentValue) : false;

        if (parsedValue !== isExpress) {
          setIsExpress(parsedValue);
        }
      } catch (e) {
        console.error('Error during polling check', e);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [isExpress]);

  const url = useMemo(() => {
    const return_url = `${window.location.protocol}//${window.location.host}/request-form/confirmation/${request.case_id}`;
    const params = {
      RETURN_URL: window.btoa(return_url),
      tid,
      referenceId: request.case_id,
      contact_forename: request.first_name,
      contact_surname: request.last_name,
      contact_company: request.companies[0].name,
      contact_street: `${request.address_line_1}${request.address_line_2 ? `;  ${request.address_line_2}` : ''}`,
      contact_place: request.city,
      contact_postcode: request.postcode,
      contact_country: searchForLabel(countries, request.country, {
        label: 'iso_code',
        value: 'id',
      }),
      contact_email: request.email,
    };

    const queryString = Object.keys(params)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
      .join('&');

    return `${import.meta.env.VITE_PAYREXX_URL}?${queryString}`;
  }, [
    countries,
    request.address_line_1,
    request.address_line_2,
    request.case_id,
    request.city,
    request.companies,
    request.country,
    request.email,
    request.first_name,
    request.last_name,
    request.postcode,
    tid,
  ]);

  useEffect(() => {
    if (url !== previousUrlRef.current && buttonRef.current && isInitializedRef.current) {
      const buttonElement = buttonRef.current;

      buttonElement.setAttribute('data-href', url);

      if (typeof $ !== 'undefined' && $.fn && $.fn.data) {
        $(buttonElement).data('href', url);

        // If the plugin stores the URL internally in its instance
        const instance = $(buttonElement).data('payrexxModal');
        if (instance && typeof instance === 'object') {
          // Try to update the instance's internal URL (depends on plugin implementation)
          if (instance.settings && instance.settings.href) {
            instance.settings.href = url;
          }
        }
      }
      previousUrlRef.current = url;
    } else if (url !== previousUrlRef.current) {
      previousUrlRef.current = url;
    }
  }, [url]);

  useEffect(() => {
    const buttonElement = buttonRef.current;
    if (buttonElement && typeof $ !== 'undefined' && $.fn && $.fn.payrexxModal) {
      if (!isInitializedRef.current) {
        $(buttonElement).payrexxModal({
          hideObjects: ['#btn-modal-close'],
          hidden: () => onCloseRef.current?.(),
        });
        isInitializedRef.current = true;
      }
    } else {
      console.warn('PaymentButton: jQuery, payrexxModal, or button element not found for initialization.');
    }

    return () => {
      isInitializedRef.current = false;
    };
  }, []);

  // Provide the clickButton method that handles modal opening
  useImperativeHandle(
    ref,
    () => ({
      clickButton() {
        const buttonElement = buttonRef.current;

        if (buttonElement && typeof $ !== 'undefined' && isInitializedRef.current) {
          $(buttonElement).click();
        } else {
          console.error('PaymentButton: Cannot trigger click. Payrexx modal not initialized or button not ready.');
        }
      },
    }),
    []
  );

  // Render the button without a key prop to avoid remounting
  return <button className="payrexx-trigger-btn" data-href={url} ref={buttonRef} style={{ display: 'none' }} />;
});

export default PaymentButton;
